// src/pages/sso-callback-page.jsx
import { useEffect, useRef } from "react";
import { useClerk, useSignIn, useSignUp } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@heroui/react";

export default function SsoCallbackPage() {
  const clerk = useClerk();
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();
  const navigate = useNavigate();
  const hasRun = useRef(false);

  useEffect(() => {
    (async () => {
      if (!clerk.loaded || hasRun.current) return;
      hasRun.current = true;

      // Shared navigate handler for finalize() and setActive().
      const navigateFn = async ({ session, decorateUrl }) => {
        if (session?.currentTask) {
          // Session tasks (e.g. forced org selection) — not used in Loopp yet.
          console.log(session.currentTask);
          return;
        }
        const from = sessionStorage.getItem("oauth_redirect") || "/";
        sessionStorage.removeItem("oauth_redirect");
        const url = decorateUrl(from);
        if (url.startsWith("http")) {
          window.location.href = url;
        } else {
          navigate(url, { replace: true });
        }
      };

      const finalizeSignIn = () => signIn.finalize({ navigate: navigateFn });
      const finalizeSignUp = () => signUp.finalize({ navigate: navigateFn });
      const backToSignIn = () => navigate("/sign-in", { replace: true });

      try {
        // Case 1: sign-in is complete.
        if (signIn.status === "complete") {
          await finalizeSignIn();
          return;
        }

        // Case 2: sign-up used an existing account — transfer to sign-in.
        if (signUp.isTransferable) {
          await signIn.create({ transfer: true });
          if (signIn.status === "complete") {
            await finalizeSignIn();
            return;
          }
          // Needs more info (MFA etc.) — let the sign-in page handle it.
          return backToSignIn();
        }

        // Case 3: sign-in requires a configured first factor.
        if (
          signIn.status === "needs_first_factor" &&
          !signIn.supportedFirstFactors?.every((f) => f.strategy === "enterprise_sso")
        ) {
          return backToSignIn();
        }

        // Case 4: new user — transfer the sign-in attempt to a sign-up.
        if (signIn.isTransferable) {
          await signUp.create({ transfer: true });
          if (signUp.status === "complete") {
            await finalizeSignUp();
            return;
          }
          // Missing fields (e.g. required name) — collect them on /sign-in/continue.
          navigate("/sign-in/continue", { replace: true });
          return;
        }

        // Case 5: sign-up already complete.
        if (signUp.status === "complete") {
          await finalizeSignUp();
          return;
        }

        // Case 6: MFA or new password required — sign-in page handles these.
        if (
          signIn.status === "needs_second_factor" ||
          signIn.status === "needs_new_password"
        ) {
          return backToSignIn();
        }

        // Case 7: the external account is already tied to an active session on
        // this client. Activate it with setActive() — finalize() won't work here.
        if (signIn.existingSession || signUp.existingSession) {
          const sessionId =
            signIn.existingSession?.sessionId || signUp.existingSession?.sessionId;
          if (sessionId) {
            await clerk.setActive({ session: sessionId, navigate: navigateFn });
            return;
          }
        }

        // Nothing matched — bail back to sign-in rather than stranding the user.
        backToSignIn();
      } catch (err) {
        console.error(JSON.stringify(err, null, 2));
        backToSignIn();
      }
    })();
  }, [clerk, signIn, signUp, navigate]);

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-blue-400/30 to-purple-500/30">
      <div className="flex flex-col items-center gap-3">
        <Spinner size="lg" />
        <p className="text-sm text-neutral-600">Signing you in…</p>
      </div>
 
      <div id="clerk-captcha" />
    </div>
  );
}